import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  NISConvertToProgramModel,
  NisParticipantResponse,
} from "../../../../api/services/nisParticipants/models";
import { WarningConfirmationModal } from "../../../../components";
import { PAGE_URLS } from "../../../../helpers/constants";
import {
  getValueOrBlank,
  isNotEmpty,
  replaceHashWithValue,
} from "../../../../helpers/utils";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { ProgramNISParticipantsSearchResultRowValue } from "../../../programSearch/modelsAndSchemas/models";

interface ConvertToProgramNavigationProps {
  nisParticipantData:
    | ProgramNISParticipantsSearchResultRowValue
    | NisParticipantResponse;
  parentKey: string;
  confirmationKey: string;
  showConvertToProgramConfirmationModal: boolean;
  setShowConvertToProgramConfirmationModal: (value: boolean) => void;
}

export const ConvertToProgramNavigation: FC<
  ConvertToProgramNavigationProps
> = ({
  nisParticipantData,
  parentKey,
  confirmationKey,
  showConvertToProgramConfirmationModal,
  setShowConvertToProgramConfirmationModal,
}) => {
  const { commonLabels, fetchRichTexts } = useSiteCore();
  const [nisNavigationData, setNisNavigationData] =
    useState<NISConvertToProgramModel>();
  const navigate = useNavigate();

  const getRichText = (key: string) => {
    return fetchRichTexts(parentKey, key);
  };

  const handleConvertToProgramConfirmationModalClose = () => {
    setShowConvertToProgramConfirmationModal(false);
  };

  useEffect(() => {
    if (isNotEmpty(nisParticipantData)) {
      let dataForNavigation: NISConvertToProgramModel;
      if ("nisNumber" in nisParticipantData) {
        // This is to identify that data is coming from NIS Participant Search Result and not from View NIS Participant page, as both have different structure of data
        dataForNavigation = {
          id: nisParticipantData.nisNumber.id,
          nisId: nisParticipantData.nisNumber.name,
          country: {
            countryId: getValueOrBlank(nisParticipantData.country?.id),
            countryName: getValueOrBlank(nisParticipantData.country?.name),
          },
          client: {
            id: getValueOrBlank(nisParticipantData.client?.id),
            name: getValueOrBlank(nisParticipantData.client?.name),
          },
        };
      } else {
        dataForNavigation = {
          id: nisParticipantData?.nisParticipantId,
          nisId: nisParticipantData.profile.nisId,
          country: nisParticipantData.professionalDetails.country,
          client: nisParticipantData.professionalDetails.employer ?? {
            id: "",
            name: "",
          },
        };
      }
      setNisNavigationData(dataForNavigation!);
    }
  }, [nisParticipantData]);

  const handleConvertToProgramConfirmationModalPrimaryButtonClick = () => {
    navigate(PAGE_URLS.CREATE_PROGRAM, {
      replace: true,
      state: {
        isFromNISParticipant: true,
        nisParticipantData: nisNavigationData,
      },
    });
  };

  return (
    <>
      {showConvertToProgramConfirmationModal && (
        <WarningConfirmationModal
          show={showConvertToProgramConfirmationModal}
          modalTitle={commonLabels.confirmation}
          onClose={handleConvertToProgramConfirmationModalClose}
          onPrimaryButtonClick={
            handleConvertToProgramConfirmationModalPrimaryButtonClick
          }
          labelContent={replaceHashWithValue(
            getRichText(confirmationKey),
            getValueOrBlank(nisNavigationData?.nisId),
          )}
          primaryButtonTitle={commonLabels.proceed}
          secondaryButtonTitle={commonLabels.no}
          isLabelHTMLContent={true}
        />
      )}
    </>
  );
};
